import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { cn } from "../../lib/cn";

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  strokeColor?: string;
  fillColor?: string;
  strokeWidth?: number;
  showTooltip?: boolean;
  format?: (value: number, index: number) => string;
  className?: string;
}

function buildPath(data: number[], width: number, height: number, padding: number): string {
  if (data.length < 2) return "";

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const scaleX = (width - padding * 2) / (data.length - 1);
  const scaleY = (height - padding * 2) / range;

  return data
    .map((val, i) => {
      const x = padding + i * scaleX;
      const y = height - padding - (val - min) * scaleY;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
}

function buildAreaPath(data: number[], width: number, height: number, padding: number): string {
  if (data.length < 2) return "";
  const linePath = buildPath(data, width, height, padding);
  const lastX = padding + (data.length - 1) * ((width - padding * 2) / (data.length - 1));
  return `${linePath} L ${lastX.toFixed(1)} ${height} L ${padding} ${height} Z`;
}

function Sparkline({
  data,
  width = 120,
  height = 32,
  strokeColor = "var(--color-lever-orange)",
  fillColor,
  strokeWidth = 1.5,
  showTooltip = true,
  format = (v) => v.toLocaleString(),
  className,
}: SparklineProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const padding = 2;

  const linePath = useMemo(() => buildPath(data, width, height, padding), [data, width, height]);
  const areaPath = useMemo(() => buildAreaPath(data, width, height, padding), [data, width, height]);

  if (data.length < 2) {
    return <div className={cn("inline-block", className)} style={{ width, height }} />;
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const scaleX = (width - padding * 2) / (data.length - 1);
  const scaleY = (height - padding * 2) / range;

  return (
    <div className={cn("relative inline-block", className)} style={{ width, height }}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {fillColor && (
          <motion.path
            d={areaPath}
            fill={fillColor}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.15 }}
            transition={{ duration: 0.3 }}
          />
        )}
        <motion.path
          d={linePath}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />

        {showTooltip && data.map((_val, i) => {
          const x = padding + i * scaleX;
          return (
            <rect
              key={i}
              x={x - scaleX / 2}
              y={0}
              width={scaleX}
              height={height}
              fill="transparent"
              onMouseEnter={() => setHoveredIndex(i)}
            />
          );
        })}

        {hoveredIndex !== null && (() => {
          const x = padding + hoveredIndex * scaleX;
          const y = height - padding - (data[hoveredIndex] - min) * scaleY;
          return (
            <circle
              cx={x}
              cy={y}
              r={3}
              fill={strokeColor}
              className="pointer-events-none"
            />
          );
        })()}
      </svg>

      {showTooltip && hoveredIndex !== null && (
        <div
          className="absolute -top-7 z-10 whitespace-nowrap rounded-[var(--radius-sm)] bg-[var(--bg-surface-solid)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-primary)] shadow-[var(--shadow-md)] border border-[var(--border-hairline)]"
          style={{
            left: padding + hoveredIndex * scaleX,
            transform: "translateX(-50%)",
          }}
        >
          {format(data[hoveredIndex], hoveredIndex)}
        </div>
      )}
    </div>
  );
}

Sparkline.displayName = "Sparkline";

export { Sparkline };
export type { SparklineProps };
